'use client'

/**
 * StockPriceHero — the live price + day-change that belongs at the TOP of a ticker hub.
 *
 * 🚨 WHY THIS EXISTS. /stocks/[symbol] is the site's #1 Google entry and ~100% bounce, yet the ONE
 * number every visitor arrives for — the price — was not in the hero; it lived two screens down inside
 * WhyMovingHeadline. This puts a large live price + change chip + session badge beside the ticker, so
 * the page answers its own question above the fold.
 *
 * ⭐ SAME SOURCE AS WhyMovingHeadline, ON PURPOSE. Both read the live Polygon quote from
 * /api/watchlist/quotes and derive Daily / Premarket / After-hours the same way, so the hero and the
 * "why moving" card can never show two different numbers. Server price/prevClose seed the first paint,
 * so the price is in the SSR HTML (SEO + no empty flash) and the live quote only refines it.
 */

import { useEffect, useState } from 'react'

type Session = 'pre' | 'rth' | 'post' | 'closed'
type Q = { chgPct: number | null; session?: Session; regClose?: number | null; prevClose?: number | null; price?: number | null }

const fmtPx = (n: number) => (n < 1 ? n.toFixed(3) : n.toFixed(2))
const signed = (n: number) => `${n >= 0 ? '+' : ''}${n.toFixed(2)}`
const signedDollars = (n: number) => `${n >= 0 ? '+' : '-'}$${fmtPx(Math.abs(n))}`

const SESSIONS: Record<Session, { label: string; dot: string; text: string; ring: string }> = {
  rth:    { label: 'Market open',   dot: 'bg-emerald-400', text: 'text-emerald-700 dark:text-emerald-300', ring: 'border-emerald-500/30 bg-emerald-500/10' },
  pre:    { label: 'Pre-market',    dot: 'bg-amber-400',   text: 'text-amber-700 dark:text-amber-300',     ring: 'border-amber-500/30 bg-amber-500/10' },
  post:   { label: 'After hours',   dot: 'bg-sky-400',     text: 'text-sky-700 dark:text-sky-300',         ring: 'border-sky-500/30 bg-sky-500/10' },
  closed: { label: 'Market closed', dot: 'bg-zinc-500',    text: 'text-muted-foreground',                  ring: 'border-border bg-muted' },
}

export default function StockPriceHero({
  symbol, initialPrice, initialPrevClose, initialSession, initialRegClose = null,
}: {
  symbol: string; initialPrice: number | null; initialPrevClose: number | null
  /** 🚨 Server-seeded session (quoteSession(), the same function the quotes API uses) + regular close. Without
   *  them the first paint had no session badge and no "Pre-market"/"After hours" chip label, and that width
   *  arrived with the quote: the column grew 181 -> 332px and slid 151px left, the dominant /stocks/[symbol]
   *  desktop layout shift (register ticker-hero-price-block-residual-cls-1102). Pass both or neither. */
  initialSession?: Session; initialRegClose?: number | null
}) {
  const [q, setQ] = useState<Q | null>(
    initialPrice != null ? { price: initialPrice, prevClose: initialPrevClose, chgPct: null, session: initialSession, regClose: initialRegClose } : null,
  )

  useEffect(() => {
    let alive = true
    const load = () =>
      fetch(`/api/watchlist/quotes?symbols=${encodeURIComponent(symbol)}`, { cache: 'no-store' })
        .then((r) => (r.ok ? r.json() : null))
        .then((j) => { if (alive) { const it = Array.isArray(j?.quotes) ? j.quotes[0] : null; if (it) setQ(it) } })
        .catch(() => {})
    load()
    const iv = setInterval(() => { if (!document.hidden) load() }, 15_000)
    return () => { alive = false; clearInterval(iv) }
  }, [symbol])

  const price = q?.price ?? initialPrice ?? null
  const pc = q?.prevClose ?? initialPrevClose ?? null
  const rc = q?.regClose ?? null
  const sess = q?.session

  // Daily = regular-session change vs prior close. Before the live quote arrives, seed a rough daily
  // move from the server price so the first paint is not blank; the quote refines it within ~15s.
  const dailyPct = rc != null && pc != null && pc > 0 ? ((rc - pc) / pc) * 100
    : price != null && pc != null && pc > 0 ? ((price - pc) / pc) * 100 : null
  const extBase = sess === 'pre' ? pc : rc != null && rc > 0 ? rc : pc
  const extPct = (sess === 'pre' || sess === 'post') && price != null && extBase != null && extBase > 0
    ? ((price - extBase) / extBase) * 100 : null

  // The headline change is the one that matters right now: extended in pre/post, otherwise the day move.
  // After hours is measured from TODAY regular close. With no regular close (a name that did not trade the
  // session, a halt, a vendor gap) the base would silently fall back to the prior close and label the WHOLE
  // day move "After hours". No label beats a wrong label: fall through to the unlabelled day move. This guards
  // the live quote too, which carries the same null (deploy-window review, ticker-hero-price-block-residual-cls-1102).
  const showExt = (sess === 'pre' || (sess === 'post' && rc != null && rc > 0)) && extPct != null
  const headPct = showExt ? extPct : dailyPct
  // The label names the EXTENDED move, so it shows only when that move is what the chip holds.
  const headLabel = !showExt ? '' : sess === 'pre' ? 'Pre-market' : 'After hours'
  const headBase = showExt ? extBase : pc
  const headAmt = headPct != null && headBase != null ? (headPct / 100) * headBase : null

  if (price == null) {
    return (
      <div className="text-right">
        <div className="text-sm font-medium text-muted-foreground">Price unavailable</div>
      </div>
    )
  }

  const up = (headPct ?? 0) >= 0
  const chip = up ? 'text-emerald-700 dark:text-emerald-300 border-emerald-500/30 bg-emerald-500/10' : 'text-rose-600 dark:text-rose-300 border-rose-500/30 bg-rose-500/10'
  const badge = sess ? SESSIONS[sess] : null

  return (
    <div className="flex flex-col items-start gap-1.5 sm:items-end">
      <div className="flex items-baseline gap-1 leading-none">
        <span className="text-lg font-semibold text-muted-foreground">$</span>
        <span className="text-4xl font-black tabular-nums tracking-tight text-foreground">{fmtPx(price)}</span>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        {headPct != null && (
          <span className={`inline-flex items-center gap-1.5 rounded-lg border px-2.5 py-1 text-sm font-bold tabular-nums ${chip}`}>
            <span className="text-[11px]">{up ? '▲' : '▼'}</span>
            {headLabel && <span className="text-[11px] font-semibold uppercase tracking-wide opacity-80">{headLabel}</span>}
            {signed(headPct)}%
            {headAmt != null && <span className="font-semibold opacity-70">{signedDollars(headAmt)}</span>}
          </span>
        )}
        {badge && (
          <span className={`inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-[11px] font-semibold ${badge.ring} ${badge.text}`}>
            <span className={`h-1.5 w-1.5 rounded-full ${badge.dot} ${sess === 'rth' ? 'animate-pulse' : ''}`} />
            {badge.label}
          </span>
        )}
      </div>
    </div>
  )
}
